import * as Sentry from '@sentry/react';
import { List as F7List, ListItem as F7ListItem, Fab, ListInput, f7 } from 'framework7-react';
import { Input as F7Input } from 'framework7-react';
import { FC, Fragment, useMemo, useState } from 'react';

import { PublicFeedEvent } from '@shared/types/event';
import { event } from 'cypress/types/jquery';
import * as dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { groupBy } from 'lodash';

import {
  FeedEvent,
  useCurrentUserEventRsvpsQuery,
  useEventQuery,
  useUnauthorizedEventsQuery,
} from '@queries/EventQueries';
import { useCurrentUserSeriesPlannerPlansQuery, useSeriesByTagSlug } from '@queries/TagQueries';
import { useCurrentUser, useInterestQuery } from '@queries/UserQueries';

import { useUpdateSeriesPlannerAdditionalPlansMutation } from '@mutations/TagMutations';

import { useEventMetaFilter } from '@hooks/EventHooks/useEventMetaFilter';
import { useAnalytics } from '@hooks/useAnalytics';
import { useEventFilters } from '@hooks/useEventFilters';
import { useReferredBy } from '@hooks/useReferredBy';

import { useLocation, useNavigate, useParams, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getDatesInRange } from '@utilities/getDatesInRange';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ChipRadioOptions } from '@components/chips/ChipRadioOptions';
import { EventChip } from '@components/chips/EventChip';
import { DatePicker } from '@components/form/DatePicker';
import { DateRangePicker } from '@components/form/DateRangePicker';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { SearchField } from '@components/form/SearchField/SearchField';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { List } from '@components/layout/List/List';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import prideSvgUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/pride.svg';
import { useEventRsvpActions } from '@screens/event/EventRsvpScreen/hooks/useEventRsvpActions';

import { EventRsvpPopup } from './components/EventRsvpPopup';

const RsvpCardListItemActions: FC<{ event: PublicFeedEvent; onInviteClick?: () => void }> = ({
  event,
  onInviteClick,
}) => {
  const { rsvp, handleMarkAttending, handleMarkInterested } = useEventRsvpActions({
    eventId: event._id,
    eventTitle: event.title,
  });

  const isCurrentUserAttending = rsvp?.rsvp === 'yes';
  const isCurrentUserInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

  return (
    <div className="mt-2 border-0 pt-0" style={{ fontSize: 12 }}>
      <div className="d-flex gap-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkAttending();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserAttending ? 'fas' : 'far'}
              name={isCurrentUserAttending ? 'calendar-check' : 'calendar-plus'}
              style={{ color: isCurrentUserAttending ? 'var(--color-green)' : undefined }}
            />
          </div>
          <div>
            <span style={{ display: 'inline-block' }}>Attending</span>
          </div>
        </div>
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkInterested();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserInterested ? 'fas' : 'far'}
              name="star"
              style={{ color: isCurrentUserInterested ? 'var(--color-yellow)' : undefined }}
            />
          </div>
          <div>
            <span style={{ display: 'inline-block' }}>Interested</span>
          </div>
        </div>
        {onInviteClick && (
          <div
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onInviteClick();
            }}
            className="d-flex align-items-center gap-2"
          >
            <div style={{ fontSize: 14 }}>
              <FontAwesomeIcon faStyle="far" name="paper-plane" />
            </div>
            <div>
              <span style={{ display: 'inline-block' }}>Invite</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FILTER_VALUES = ['isDanceParty', 'isSapphic', 'isDragShow', 'isPrideWeekend'];

interface SeriesPlannerScreenProps {
  startDate: string;
  endDate: string;
  tagSlug: string;
}

export const SeriesPlannerScreen = createScreenComponent<SeriesPlannerScreenProps>(
  ({ tagSlug, startDate, endDate, back, f7route }) => {
    const { track } = useAnalytics();
    const { series } = useSeriesByTagSlug(tagSlug);
    const { navigate, back: navigateBack } = useRouter();
    const { updateSeriesPlannerAdditionalPlansAsync } =
      useUpdateSeriesPlannerAdditionalPlansMutation();
    const { currentUser } = useCurrentUser();
    const [selectedEvents, setSelectedEvents] = useState<
      Record<string, 'attending' | 'interested' | undefined>
    >({});
    const [dateAdditionalPlans, setDateAdditionalPlans] = useState<Record<string, string>>({});

    const { seriesPlannerPlansAreReady, seriesPlannerPlans } =
      useCurrentUserSeriesPlannerPlansQuery();
    const prideWeekendPlannerPlans = useMemo(() => {
      return seriesPlannerPlans?.[series?._id]?.find(
        (plan) => plan.startDate === startDate && plan.endDate === endDate,
      );
    }, [seriesPlannerPlans, series?._id, startDate, endDate]);

    const { createAuthGateHandler } = useAuthGate();

    const [currentEvent, setCurrentEvent] = useState<PublicFeedEvent | undefined>(undefined);
    const [currentDate, setCurrentDate] = useState(startDate);
    const dates = useMemo(() => {
      return getDatesInRange(startDate, endDate);
    }, [startDate, endDate]);

    const nextDate = useMemo(() => {
      const nextDate = dayjs(currentDate).add(1, 'day').format('YYYY-MM-DD');
      return dates.includes(nextDate) ? nextDate : undefined;
    }, [currentDate, dates]);

    const handleContinueClick = () => {
      track('Click Continue', { nextDate });

      createAuthGateHandler(
        async () => {
          const didCompletePlanner = !nextDate;

          try {
            updateSeriesPlannerAdditionalPlansAsync({
              seriesId: series?._id,
              startDate,
              endDate,
              additionalPlans: {
                [currentDate]:
                  dateAdditionalPlans[currentDate] ??
                  prideWeekendPlannerPlans?.dates?.[currentDate]?.additionalPlans,
              },
              didCompletePlanner,
            }).then(() => {
              if (!nextDate) {
                navigate(`/noauth/ucc/${currentUser?._id}`, {
                  state: { back: `/noauth/series/${tagSlug}/?tab=rsvpToEvents` },
                });
                return;
              }
            });
          } catch (error) {
            Sentry.captureException(error);
          }

          if (nextDate) {
            setCurrentDate(nextDate);
          }
        },
        {
          authTitle: `Continue to ${dayjs(nextDate).format('dddd')} Plans`,
          authMessage: `Enter your phone number to save your planner progress.`,
        },
      );
    };

    const continueButtonText = useMemo(() => {
      if (!nextDate) {
        return 'Finish Planning';
      }

      return `Continue to ${dayjs(nextDate).format('dddd')} Plans`;
    }, [nextDate]);

    const {
      eventsAreReady,
      events = [],
      eventsMeta,
    } = useUnauthorizedEventsQuery(
      {
        startDate,
        endDate,
        tagIds: [series?._id],
        forceSupply: 'true',
        rsvpData: 'ignore',
      },
      Boolean(series?.city?._id && series._id),
    );
    const { eventRsvps } = useCurrentUserEventRsvpsQuery();

    const eventsGroupedByDate = useMemo(() => {
      return groupBy(events, 'startDate');
    }, [events]);

    const { updateFilterValue, currentFilter, filteredEvents } = useEventMetaFilter(
      eventsGroupedByDate?.[currentDate] ?? [],
      eventsMeta?.filters ?? [],
      f7route.query?.filter,
    );

    const { attendingCount, interestedCount } = useMemo(() => {
      if (!eventRsvps) {
        return {
          attendingCount: 0,
          interestedCount: 0,
        };
      }

      const currentDateEvents = eventsGroupedByDate?.[currentDate] ?? [];

      return {
        attendingCount:
          currentDateEvents?.filter((event) => eventRsvps[event._id]?.rsvp === 'yes').length ?? 0,
        interestedCount:
          currentDateEvents?.filter(
            (event) =>
              eventRsvps[event._id]?.rsvp !== 'yes' &&
              eventRsvps[event._id]?.eventInterest === 'interested',
          ).length ?? 0,
      };
    }, [eventsGroupedByDate, currentDate, eventRsvps]);

    const handleEventClick = (event: PublicFeedEvent) => {
      track('Click Event', { eventId: event._id });
      setCurrentEvent(event);
    };

    return (
      <Screen
        name="Series Screen"
        hideToolbar
        headerProps={{
          back: { to: back ?? `/noauth/series/${tagSlug}?tab=rsvpToEvents` },
          title: <Screen.GagglTitle />,
        }}
        bottomToolbar={
          <Screen.BottomToolbar height={78} maxWidth={425}>
            <div className="w-100 pb-1">
              <div className="mb-1" style={{ fontSize: '12px', opacity: 0.7, textAlign: 'center' }}>
                <strong>{dayjs(currentDate).format('dddd, MMMM Do')}</strong>
                {' — '}
                <span>
                  {[`${attendingCount} attending`, `${interestedCount} interested`].join(' • ')}
                </span>
              </div>
              <Button
                roundness="rounded"
                endIcon={<FontAwesomeIcon name="chevron-right" />}
                onClick={handleContinueClick}
              >
                {continueButtonText}
              </Button>
            </div>
          </Screen.BottomToolbar>
        }
      >
        <Screen.Content maxWidth={425}>
          <TitleToolbar
            text={<div style={{ fontSize: '1.75rem' }}>Denver Pride Planner</div>}
            className="mb-0"
          />

          <div className="mb-2">
            <CopyText
              text="https://denverqueercalendar.com/pride-2026"
              displayText="denverqueercalendar.com/pride-2026"
              clickAction="share"
              buttonText={{ copy: 'Share' }}
            />
          </div>

          <p className="mt-0">
            There are always so many parties happening for Pride Weekend. Use this planner to help
            you find what's going on and keep track of everything you want to do.
          </p>

          <TitleToolbar
            text={`What are your plans for ${dayjs(currentDate).format('dddd, MMMM Do')}?`}
            size="sm"
            infoButton={{
              title: 'How to use the planner',
              text: 'Press "Attending" or "Interested" when you find an event you want to add to your planner, and use the filters to find events that match your interests.',
            }}
          />

          {eventsAreReady ? (
            <>
              <div className="mb-2">
                <HorizontalScroll
                  items={[
                    <div className="d-flex gap-1">
                      <EventChip
                        label="All"
                        startIcon={
                          <img
                            src={prideSvgUrl}
                            style={{
                              height: 14,
                              width: 14,
                              objectFit: 'contain',
                              display: 'block',
                            }}
                          />
                        }
                        variant={!currentFilter ? undefined : 'outlined'}
                        onClick={() => updateFilterValue(undefined)}
                        size="md"
                      />
                      {eventsMeta?.filters
                        ?.filter((filter) => FILTER_VALUES.includes(filter.value))
                        ?.map((filter) => (
                          <EventChip
                            key={filter.value}
                            label={filter.label}
                            startIcon={
                              <img
                                src={filter.cta?.suggestion?.iconUrl}
                                style={{
                                  height: 14,
                                  width: 14,
                                  objectFit: 'contain',
                                  display: 'block',
                                }}
                              />
                            }
                            variant={currentFilter?.value === filter.value ? undefined : 'outlined'}
                            size="md"
                            onClick={() => updateFilterValue(filter.value)}
                          />
                        ))}
                    </div>,
                  ]}
                />
              </div>

              {filteredEvents.length ? (
                <F7List
                  className="mt-0"
                  dividersIos
                  outlineIos
                  mediaList
                  style={{ marginLeft: '-1rem', marginRight: '-1rem' }}
                >
                  {filteredEvents.map((event) => (
                    <F7ListItem
                      key={event._id}
                      title={event.title}
                      subtitle={event.timeDetails.split(' at ')[1]}
                    >
                      <div slot="media" onClick={() => handleEventClick(event)}>
                        <img
                          src={event.img}
                          alt={event.title}
                          style={{ width: 54, height: 72, objectFit: 'cover', borderRadius: 4 }}
                        />
                      </div>
                      <div
                        slot="after"
                        style={{ fontSize: '14px' }}
                        role="button"
                        onClick={() => handleEventClick(event)}
                      >
                        More
                      </div>
                      <div slot="text">
                        <RsvpCardListItemActions event={event} />
                      </div>
                    </F7ListItem>
                  ))}
                  <F7ListItem>
                    <div>
                      <FormLabel text="+ Anything we missed?" />
                      {seriesPlannerPlansAreReady ? (
                        <div className="mt-1">
                          <Textarea
                            key={currentUser?._id ?? 'unauthenticated'}
                            placeholder={`Add some plans for ${dayjs(currentDate).format('dddd')}`}
                            defaultValue={
                              prideWeekendPlannerPlans?.dates?.[currentDate]?.additionalPlans ||
                              dateAdditionalPlans[currentDate]
                            }
                            onChange={(value) =>
                              setDateAdditionalPlans((prev) => ({ ...prev, [currentDate]: value }))
                            }
                          />
                        </div>
                      ) : (
                        <>
                          <Skeleton height={120} />
                        </>
                      )}
                    </div>
                  </F7ListItem>
                </F7List>
              ) : (
                currentFilter && (
                  <EmptyState
                    title="No matching events"
                    text="No events match this filter"
                    button={
                      eventsGroupedByDate?.[currentDate]?.length
                        ? { text: 'See all events', onClick: () => updateFilterValue(undefined) }
                        : undefined
                    }
                  />
                )
              )}
            </>
          ) : (
            <div className="d-grid gap-2">
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
            </div>
          )}
        </Screen.Content>

        <EventRsvpPopup
          isVisible={!!currentEvent}
          event={currentEvent}
          onClose={() => setCurrentEvent(undefined)}
        />
      </Screen>
    );
  },
);
